<template>
  <v-container>
    <v-form ref="form" :lazy-validation="true">
      <validation-observer ref="validationObserver" v-slot="{ invalid }">
        <v-row class="mt-n8 mb-4">
          <v-col cols="12">
            <span class="headline pb-4">{{ $t("form.subTitleGroup") }}</span>
          </v-col>
        </v-row>
        <v-row class="mt-n8 mb-2">
          <v-col cols="12">
            <validation-provider
              :name="$t('form.fields.group')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                ref="groupSelector"
                :value="selectedGroup"
                :items="freeGroups"
                item-value="_id"
                :label="$t('form.fields.group') + ' *'"
                required
                outlined
                @change="setSelectedGroup"
                :error-messages="errors"
              >
                <template v-slot:selection="{ item }">
                  <selection-view :item="item" />
                </template>
                <template v-slot:item="{ item }">
                  <selection-view :item="item" />
                </template>
              </v-select>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-4">
          <v-col cols="12">
            <span class="headline pb-4">{{
              $t("form.subTitleContactPerson")
            }}</span>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.firstname')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="firstname"
                :label="$t('form.fields.firstname') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.lastname')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="lastname"
                :label="$t('form.fields.lastname') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.email')"
              v-slot="{ errors }"
              vid="originalEmail"
              rules="required|email|confirmed:@confirmationEmail"
            >
              <v-text-field
                ref="originalMail"
                v-model="email"
                :label="$t('form.fields.email') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.confirmationEmail')"
              v-slot="{ errors }"
              vid="confirmationEmail"
              rules="required|email|confirmed:@originalEmail"
            >
              <v-text-field
                v-model="confirmationEmail"
                :label="$t('form.fields.confirmationEmail') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-4">
          <v-col cols="12">
            <span class="headline pb-4">{{ $t("form.subTitleCompany") }}</span>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.vendorName')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="vendorName"
                outlined
                :label="$t('form.fields.vendorName') + ' *'"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="companyUid"
              outlined
              :label="$t('form.fields.companyUid')"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.companyStreet')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="companyStreet"
                outlined
                :label="$t('form.fields.companyStreet') + ' *'"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.companyPostalCode')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="companyPostalCode"
                outlined
                :label="$t('form.fields.companyPostalCode') + ' *'"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.companyTown')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="companyTown"
                outlined
                :label="$t('form.fields.companyTown') + ' *'"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.companyCountry')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-autocomplete
                :label="$t('form.fields.companyCountry') + ' *'"
                outlined
                :items="countries"
                item-text="value"
                item-value="key"
                :error-messages="errors"
                v-model="companyCountry"
              />
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="mt-n8">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.website')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="vendorUrl"
                :label="$t('form.fields.website') + ' *'"
                required
                outlined
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.logo')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-file-input
                v-model="logo"
                :label="$t('form.fields.logo') + ' *'"
                accept=".png,.jpg,.jpeg"
                :loading="loading"
                required
                outlined
                :error-messages="errors"
                @change="setLogo($event)"
              ></v-file-input>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="my-n12">
          <v-col>
            <div id="mcaptcha__widget-container" />
          </v-col>
        </v-row>

        <v-layout>
          <v-spacer></v-spacer>
          <v-btn
            :disabled="invalid"
            color="primary"
            class="mr-4"
            :loading="loading"
            v-on="on"
            @click="submit()"
          >
            {{ $t("form.btn.send") }}</v-btn
          >
        </v-layout>

        <v-dialog v-model="dialog" width="500">
          <v-card>
            <v-card-title class="white--text headline primary" primary-title>
              {{ $t("form.confirmationDialog.title") }}
            </v-card-title>

            <v-card-text class="pa-md-4">
              {{ $t("form.confirmationDialog.confirmationText") }}
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="reload">
                {{ $t("form.confirmationDialog.buttonText") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </validation-observer>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SelectionView from "./SponsorForm/SelectionView";
import {
  attachmentResource,
  verificationResource,
  mcaptchaSettingsResource,
} from "../api/resources";
import de from "../i18n/de.json";

export default {
  data: () => ({
    id: "",
    firstname: "",
    lastname: "",
    email: "",
    confirmationEmail: "",
    vendorName: "",
    companyStreet: "",
    companyPostalCode: "",
    companyTown: "",
    companyCountry: "",
    companyUid: "",
    vendorUrl: "",
    dialog: false,
    logo: null,
    loading: false,
  }),
  async mounted() {
    const captchaSettings = (await mcaptchaSettingsResource.get()).data;
    let config = {
      widgetLink: new URL(captchaSettings.widgetLink),
    };
    // eslint-disable-next-line no-undef
    new mcaptchaGlue.default(config);
  },
  methods: {
    ...mapActions(["submitReservation", "setSelectedGroup"]),
    async submit() {
      const verificationToken = document.getElementById("mcaptcha__token")
        .value;
      if (!verificationToken) {
        return;
      }
      await verificationResource.post("", {
        token: verificationToken,
      });
      this.loading = true;
      let formData = new FormData();
      formData.append("file", this.logo);
      const data = await attachmentResource.post("", formData, {
        headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
      });
      const logoData = data.data;

      const reservation = {
        sponsorGroupId: this.selectedGroup,
        sponsorGroup: this.selectedGroup.title,
        firstname: this.firstname,
        lastname: this.lastname,
        email: this.email,
        vendorName: this.vendorName,
        companyUid: this.companyUid,
        companyStreet: this.companyStreet,
        companyPostalCode: this.companyPostalCode,
        companyTown: this.companyTown,
        companyCountry: this.companyCountry,
        vendorUrl: this.vendorUrl,
        logo: logoData,
      };
      await this.submitReservation(reservation);
      this.loading = false;
      this.dialog = true;
      this.clear();
    },
    setLogo($data) {
      this.logo = $data;
    },
    clear() {
      this.firstname = "";
      this.lastname = "";
      this.email = "";
      this.confirmationEmail = "";
      this.vendorName = "";
      this.companyStreet = "";
      this.companyPostalCode = "";
      this.companyTown = "";
      this.companyCountry = "";
      this.companyUid = "";
      this.vendorUrl = "";
      this.logo = null;
      this.$refs.validationObserver.reset();
    },
    selectGroup(e) {
      this.setSelectedGroup(
        this.freeGroups.find((element) => element._id == e.target.dataset.value)
      );
    },
    reload() {
      location.reload();
    },
  },
  computed: {
    ...mapGetters(["freeGroups", "selectedGroup"]),
    sponsorGroups() {
      return window.sponsorGroups || [];
    },
    countries() {
      return Object.keys(de.countries).map((countryKey) => {
        return {
          key: countryKey,
          value: this.$t(`countries.${countryKey}`),
        };
      });
    },
  },
  created: function() {
    const selectButtons = document.getElementsByClassName("sponsor-select");
    for (let i = 0; i < selectButtons.length; i++) {
      selectButtons[i].addEventListener("click", this.selectGroup);
    }
  },
  destroyed: function() {
    const selectButtons = document.getElementsByClassName("sponsor-select");
    for (let i = 0; i < selectButtons.length; i++) {
      selectButtons[i].removeEventListener("click", this.selectGroup);
    }
  },
  components: {
    SelectionView,
  },
};
</script>
